import { MODEL_PARAMETER_NAME, OnnxResultType, OutputType, getPositionalInputName } from '@/tools/onnx/onnx.constants';
import { SUPPORT_LINK } from '@/utilities/externalLinks';
import { errorToast } from '@/utilities/toast.utilities';
import { SearchItemsOrderByEnum as OrderByEnum, sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { SeeqNames } from '@seeqdev/constants';
import type { ToolParameter } from '@/tools/tool.types';

type Period = { value: number; units: string };

const MAX_NO_OF_ONNX_ITEMS = 1000;

type BuildFormulaInput = {
  formulaItemId: string;
  onnxResultType: OnnxResultType;
  onnxInputParameters: OnnxInputParameter[];
  restrictToCondition: any;
  outputType: OutputType;
  outputSignal: string;
  cleanCapsules: boolean;
  capsuleMinDuration: Period;
  capsuleMergeDuration: Period;
};

export function buildFormula({
  formulaItemId,
  onnxResultType,
  onnxInputParameters,
  restrictToCondition,
  outputType,
  outputSignal,
  cleanCapsules,
  capsuleMinDuration,
  capsuleMergeDuration,
}: BuildFormulaInput): {
  formula: string;
  parameters: Record<string, string>;
} {
  let inputSignalNames: any = [];
  const parameters: Record<string, string> = {};

  for (const entry of onnxInputParameters) {
    inputSignalNames.push(`$${entry.name}`);
    parameters[entry.name] = entry.item.id!;
  }

  if (restrictToCondition) {
    parameters['restrictToCondition'] = restrictToCondition.id;
    inputSignalNames = inputSignalNames.map((signal: string) => `${signal}.within($restrictToCondition)`);
  }

  parameters[MODEL_PARAMETER_NAME] = formulaItemId;
  let formula = `$${MODEL_PARAMETER_NAME}`;

  if (onnxResultType === OnnxResultType.OnnxAnomalyModel) {
    if (outputType === OutputType.CONDITION) {
      formula = `${formula}.detect(${inputSignalNames.join(', ')})`;
      if (cleanCapsules) {
        formula = `(${formula})`;
        formula += `\n.merge(${capsuleMergeDuration.value}${capsuleMergeDuration.units})`;

        // only call removeShorterThan if we have a positive value (0 can be accepted in the UI)
        if (capsuleMinDuration.value > 0) {
          formula += `\n.removeShorterThan(${capsuleMinDuration.value}${capsuleMinDuration.units})`;
        }
      }
    } else if (outputType === OutputType.SIGNAL) {
      formula = `${formula}.toSignal('${outputSignal}', ${inputSignalNames.join(', ')})`;
    }
  } else if (onnxResultType === OnnxResultType.OnnxPredictionModel) {
    formula = `${formula}.predict(${inputSignalNames.join(', ')})`;
  }
  return { formula, parameters };
}

export const openTooltipLink = () => window.open(`${SUPPORT_LINK}/external-ml`);

export interface FormulaItem {
  id: string;
  name: string;
  description: string;
  type: string;
  isArchived: boolean;
  formula: string;
  inputs: string;
  resultType: OnnxResultType;
  singleValue: string[];
}

export interface OnnxInputParameter {
  item: { id?: string; name?: string; type?: string };
  identifier: string;
  name: string;
}

export interface ButtonItem {
  label: string;
  id: string;
  value: OutputType;
  icon: string;
}

export const fetchONNXItems = async (workbookId: string): Promise<FormulaItem[]> => {
  try {
    let allItems = [];
    let offset = 0;
    let response: any;
    do {
      const response = await sqItemsApi.searchItems({
        types: [SeeqNames.FormulaType.FormulaItem],
        includeProperties: [SeeqNames.Properties.Formula, SeeqNames.Properties.Inputs, SeeqNames.Properties.ResultType],
        filters: [
          `${SeeqNames.Properties.ResultType}==${SeeqNames.FormulaType.OnnxPredictionModel}`,
          `${SeeqNames.Properties.ResultType}==${SeeqNames.FormulaType.OnnxAnomalyModel}`,
        ],
        limit: MAX_NO_OF_ONNX_ITEMS,
        orderBy: [OrderByEnum.Name],
        scope: [workbookId],
        offset,
      });
      allItems.push(...(response?.data?.items ?? []));
      offset += 1;
    } while (response?.data?.next);

    return allItems.map(
      (item) =>
        ({
          id: item.id,
          name: item.name,
          description: item.description,
          type: item.type,
          isArchived: item.isArchived,
          formula: item.includedProperties?.Formula?.value,
          inputs: item.includedProperties?.Inputs?.value,
          resultType: item.includedProperties?.[SeeqNames.Properties.ResultType]?.value as OnnxResultType,
          singleValue: extractValuesFromFormula(item.includedProperties?.Formula?.value, 'singleValue'),
        }) as FormulaItem,
    );
  } catch (error) {
    errorToast({ httpResponseOrError: error });
    return [];
  }
};

export function extractValuesFromFormula(formula: string | undefined, keyMatch: string): string[] {
  if (!formula) {
    return [];
  }
  const match = formula.match(/options\((.*)\)/);
  if (!match) {
    return [];
  }

  const inside = match[1];
  const tokens = [...inside.matchAll(/["']([^"']+)["']/g)].map((m) => m[1]);

  const values: string[] = [];
  for (let i = 0; i < tokens.length; i += 2) {
    const key = tokens[i];
    const value = tokens[i + 1];
    if (key.startsWith(keyMatch)) {
      values.push(value);
    }
  }

  return values;
}

export const parseAndMergeInputs = (inputs: string | undefined, existingInputs: OnnxInputParameter[]) => {
  try {
    const rawInputs = inputs ?? '[]';
    const fixedJson = rawInputs.replace(/'/g, '"');

    const parsedInputs: OnnxInputParameter[] = JSON.parse(fixedJson).map((input: any, index: number) => ({
      item: { id: null, name: null, type: null },
      identifier: input.name,
      name: getPositionalInputName(index),
    }));

    return parsedInputs.map((parsedInput) => {
      const matchingInput = existingInputs.find(
        (inputParameter: { identifier: string }) => inputParameter.identifier === parsedInput.identifier,
      );
      if (matchingInput) {
        return { ...parsedInput, item: matchingInput.item };
      }
      return parsedInput;
    });
  } catch (error) {
    console.error(`Error parsing Inputs: ${inputs}`, error);
    return [];
  }
};

/**
 * Rebuilds the onnxInputParameters list by pairing each persisted `{identifier, name}` entry with the matching
 * formula-bound parameter's item, looked up by `name` (e.g. `input1`, `input2`, ... per ToolParameter).
 *
 * Older persisted entries that predate the `name` field fall back to their positional default (see
 * getPositionalInputName), which matches how buildFormula has always bound them.
 */
export function mapOnnxInputParameters(
  persistedInputParameters: { identifier: string; name?: string }[],
  formulaParameters: ToolParameter[],
): OnnxInputParameter[] {
  return (persistedInputParameters ?? []).map((persisted, index) => {
    const name = persisted.name ?? getPositionalInputName(index);
    const boundParameter = formulaParameters.find((parameter) => parameter.name === name);

    return {
      identifier: persisted.identifier,
      name,
      item: {
        id: boundParameter?.item?.id ?? null,
        name: boundParameter?.item?.name ?? null,
        type: boundParameter?.item?.type ?? null,
      },
    } as OnnxInputParameter;
  });
}

export function getFormulaItemIdFromParameters(formulaParameters: ToolParameter[]): string | null {
  return formulaParameters.find((parameter) => parameter.name === MODEL_PARAMETER_NAME)?.item?.id ?? null;
}
